/**
 * Caribbrew Subscription Journey Logic
 * Optimized for Precise SVG States, Correct Categorization, and Variant Images
 * Refined for UX: Auto-scroll, default "All" visibility.
 * Visualizer REMOVED.
 */

class SubscriptionJourney {
  constructor() {
    this.container = document.getElementById('SubscriptionJourney');
    if (!this.container) return;

    this.data = JSON.parse(document.getElementById('SubscriptionData').textContent);
    this.state = {
      productId: null,
      variantId: null,
      sellingPlanId: null,
      productTitle: '',
      variantTitle: '',
      planName: '',
      price: null,
      selectedOptions: [],
      available: true,
      currentStep: 1
    };

    // Images provided by Liquid data bridge
    this.variantImages = {
      'ground': this.data.asset_urls.ground,
      'beans': this.data.asset_urls.beans
    };

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.setupAnimations();

    // Ensure "All" products show by default
    const allTab = this.container.querySelector('.tab-btn[data-category="all"]');
    if (allTab) this.handleCategoryFilter(allTab);
  }

  setupAnimations() {
    gsap.from('.journey-header', { duration: 1.2, y: 30, opacity: 0, ease: 'power4.out' });
    gsap.from('#Step1 .selection-grid .selection-card', {
      duration: 0.8,
      y: 20,
      opacity: 0,
      stagger: 0.1,
      ease: 'power3.out',
      delay: 0.5
    });
  }

  setupEventListeners() {
    this.container.querySelectorAll('#BaseSelection .selection-card').forEach(card => {
      card.addEventListener('click', () => this.handleProductSelection(card));
    });

    this.container.querySelectorAll('.tab-btn').forEach(tab => {
      tab.addEventListener('click', () => this.handleCategoryFilter(tab));
    });

    const atcButton = document.getElementById('AddToCartButton');
    if (atcButton) atcButton.addEventListener('click', () => this.addToCart());

    // Setup FAQ Accordion behavior
    const faqItems = this.container.querySelectorAll('details.faq-item');
    faqItems.forEach(item => {
      item.addEventListener('toggle', (e) => {
        if (item.open) {
          faqItems.forEach(other => {
            if (other !== item) other.open = false;
          });
        }
      });
    });
  }

  handleCategoryFilter(tab) {
    const category = tab.dataset.category;
    this.container.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
    tab.classList.add('active');

    const cards = this.container.querySelectorAll('#BaseSelection .selection-card');
    cards.forEach(card => {
      if (category === 'all' || card.dataset.category === category) {
        card.style.display = 'block';
        gsap.to(card, { scale: 1, opacity: 1, duration: 0.4 });
      } else {
        gsap.set(card, { display: 'none', opacity: 0, scale: 0.95 });
      }
    });
  }

  handleProductSelection(card) {
    const productId = card.dataset.productId;
    const product = this.data.products.find(p => p.id == productId);
    if (!product) return;

    this.state.productId = productId;
    this.state.productTitle = product.title;
    this.state.variantId = null;
    this.state.sellingPlanId = null;
    this.state.selectedOptions = [];

    this.container.querySelectorAll('#BaseSelection .selection-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    this.renderNextOption(product, 0);
    this.showStep(2);
    this.hideStep(3);
    this.updateSummary();
  }

  renderNextOption(product, optionIndex) {
    const optionGrid = document.getElementById('VariantSelection');
    if (optionIndex === 0) optionGrid.innerHTML = '';

    const optionName = product.options[optionIndex];

    // Update main step title if it's the first option
    if (optionIndex === 0) {
      const step2Title = document.querySelector('#Step2 .step-title');
      if (step2Title) {
        step2Title.textContent = `2. Choose Your ${optionName}`;
      }
    }

    const groupContainer = document.createElement('div');
    groupContainer.className = 'option-group';
    groupContainer.id = `OptionGroup-${optionIndex}`;

    // Only show inner title if taking additional selections (e.g. Size after Grind)
    const titleHtml = optionIndex > 0 ? `<h3 class="option-group-title">Select ${optionName}</h3>` : '';
    groupContainer.innerHTML = `${titleHtml}<div class="selection-grid"></div>`;

    const grid = groupContainer.querySelector('.selection-grid');
    const filteredVariants = this.getFilteredVariants(product, optionIndex);
    const availableValues = [...new Set(filteredVariants.map(v => v.options[optionIndex]))];

    availableValues.forEach(value => {
      const card = document.createElement('div');
      card.className = 'selection-card';

      // Check if any variant with this value is actually in stock
      const hasStock = filteredVariants.some(v => v.options[optionIndex] === value && v.available);
      if (!hasStock) card.classList.add('sold-out');

      const lowerVal = value.toLowerCase();
      let imgHtml = '';
      if (lowerVal.includes('ground')) imgHtml = `<div class="card-image"><img src="${this.variantImages.ground}" alt="Ground"></div>`;
      if (lowerVal.includes('bean')) imgHtml = `<div class="card-image"><img src="${this.variantImages.beans}" alt="Beans"></div>`;

      card.innerHTML = `${imgHtml}<span>${value}${!hasStock ? ' (Sold Out)' : ''}</span>`;

      if (hasStock) {
        card.addEventListener('click', () => this.handleOptionValueSelection(product, optionIndex, value, card));
      } else {
        card.style.opacity = '0.5';
        card.style.cursor = 'not-allowed';
      }
      grid.appendChild(card);
    });

    const existingGroups = optionGrid.querySelectorAll('.option-group');
    existingGroups.forEach((group, idx) => { if (idx >= optionIndex) group.remove(); });
    optionGrid.appendChild(groupContainer);

    // Only apply a separate GSAP animation if this isn't the first option
    // (the first option rides the native showStep(2) animation)
    if (optionIndex > 0) {
      gsap.from(groupContainer, { y: 20, opacity: 0, duration: 0.5 });
      setTimeout(() => {
        this.scrollToStep(document.getElementById('Step2'));
      }, 100);
    }
  }

  handleOptionValueSelection(product, optionIndex, value, card) {
    const grid = card.parentElement;
    grid.querySelectorAll('.selection-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    this.state.selectedOptions[optionIndex] = value;
    this.state.selectedOptions.length = optionIndex + 1;

    if (optionIndex < product.options.length - 1) {
      this.renderNextOption(product, optionIndex + 1);
    } else {
      const finalVariant = product.variants.find(v => JSON.stringify(v.options) === JSON.stringify(this.state.selectedOptions));
      if (finalVariant) {
        this.state.variantId = finalVariant.id;
        this.state.variantTitle = finalVariant.title;
        this.state.price = finalVariant.price;
        this.state.available = finalVariant.available;

        if (this.state.available) {
          this.renderPlans(product);
          this.showStep(3);
        } else {
          this.hideStep(3);
          const atcButton = document.getElementById('AddToCartButton');
          if (atcButton) {
            atcButton.disabled = true;
            atcButton.textContent = 'Sold Out';
          }
        }
      }
    }
    this.updateSummary();
  }

  renderPlans(product) {
    const planGrid = document.getElementById('FrequencySelection');
    if (!planGrid) return;
    planGrid.innerHTML = '';

    product.selling_plan_groups.forEach(group => {
      group.selling_plans.forEach(plan => {
        const card = document.createElement('div');
        card.className = 'selection-card frequency-card';
        card.innerHTML = `
          <div class="card-info">
            <h3>${plan.name}</h3>
            ${plan.description ? `<p class="plan-description">${plan.description}</p>` : ''}
          </div>`;
        card.addEventListener('click', () => this.handlePlanSelection(card, plan));
        planGrid.appendChild(card);
      });
    });
    gsap.from('#Step3', { y: 30, opacity: 0, duration: 0.8 });
  }

  handlePlanSelection(card, plan) {
    this.state.sellingPlanId = plan.id;
    this.state.planName = plan.name;
    this.container.querySelectorAll('#FrequencySelection .selection-card').forEach(c => c.classList.remove('selected'));
    card.classList.add('selected');

    this.updateSummary();
    this.showStep(4);

    const atcButton = document.getElementById('AddToCartButton');
    if (atcButton) atcButton.disabled = false;
  }

  getFilteredVariants(product, optionIndex) {
    if (optionIndex === 0) return product.variants;
    return product.variants.filter(variant => {
      for (let i = 0; i < optionIndex; i++) {
        if (variant.options[i] !== this.state.selectedOptions[i]) return false;
      }
      return true;
    });
  }

  showStep(num) {
    const step = document.getElementById(`Step${num}`);
    if (step && step.classList.contains('hidden')) {
      step.classList.remove('hidden');
      gsap.fromTo(step, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1, ease: 'expo.out' });
    }

    // Smooth scroll to step with a slight delay
    setTimeout(() => {
      // For Step 2 option groups (like Grind), we want to scroll to the top of Step 2 entirely
      if (num === 2) {
        this.scrollToStep(step);
      } else if (step) {
        this.scrollToStep(step);
      }
    }, 100);
  }

  hideStep(num) {
    const step = document.getElementById(`Step${num}`);
    if (step) step.classList.add('hidden');
    const atcButton = document.getElementById('AddToCartButton');
    if (atcButton) atcButton.disabled = true;
  }

  /**
   * @param {HTMLElement | Element | null} element 
   */
  scrollToStep(element) {
    if (!element) return;
    // Calculate offset considering sticky headers
    const headerNode = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
    const stickyHeaderHeight = headerNode ? headerNode.getBoundingClientRect().height : 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - stickyHeaderHeight - 20;

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }

  updateSummary() {
    const selectionText = document.querySelector('.final-review-box .current-selection');
    const priceText = document.querySelector('.final-review-box .current-price');

    if (this.state.productId) {
      let text = this.state.productTitle;
      if (this.state.selectedOptions.length > 0) text += ` - ${this.state.selectedOptions.join(' / ')}`;
      if (this.state.planName) text += ` (${this.state.planName})`;
      if (selectionText) selectionText.textContent = text;
      if (this.state.price && priceText) {
        const originalPrice = this.formatMoney(this.state.price);
        const subPrice = this.formatMoney(Math.round(this.state.price * 0.85));
        priceText.innerHTML = `
          <span class="price-compare">${originalPrice}</span>
          <span class="price-subscribe">${subPrice} <span class="price-badge">15% off</span></span>
        `;
      }
    }
  }

  async addToCart() {
    const button = document.getElementById('AddToCartButton');
    if (!button) return;
    button.disabled = true;
    button.textContent = 'Processing...';

    const formData = {
      items: [{
        id: this.state.variantId,
        quantity: 1,
        selling_plan: this.state.sellingPlanId,
        properties: { '_journey_origin': 'Premium Journey V2' }
      }]
    };

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (response.ok) window.location.href = '/checkout';
      else {
        const error = await response.json();
        alert('Error: ' + (error.description || error.message));
        button.disabled = false;
        button.textContent = 'Subscribe & Save 15%';
      }
    } catch (e) {
      console.error('Failed', e);
      button.disabled = false;
      button.textContent = 'Subscribe & Save 15%';
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
  }
}

document.addEventListener('DOMContentLoaded', () => { new SubscriptionJourney(); });
